import React, { useEffect, useState } from 'react';

import { ArrowLeft, Check, ChevronLeft, ChevronRight, X } from 'lucide-react';

import {
	useCreateEnhancedCustomRatesMutation,
	useGetDefaultRatesQuery,
	useGetEnhancedCustomRatesQuery,
	useUpdateEnhancedCustomRatesMutation,
} from '../../api';
import { useToast } from '../../context/ToastContext';
import {
	createDefaultPricingConfiguration,
	transformEnhancedResponseToWizard,
	transformWizardToEnhancedRequest,
} from '../../utils/enhancedApiTransformer';

import BasicInfoStep from './wizard/BasicInfoStep';
import { DiscountRateStep } from './wizard/DiscountRateStep';
import MonthlyAndBatchFeesStep from './wizard/MonthlyAndBatchFeesStep';
import PassThroughCostsStep from './wizard/PassThroughCostsStep';
import PricingModelStep from './wizard/PricingModelStep';

import type { PricingConfiguration } from '../../types';

interface PricingConfigurationWizardProps {
	isOpen: boolean;
	onClose: () => void;
	onComplete: (configId: string) => void;
	editConfigId?: string;
	isFullPage?: boolean;
	onBack?: () => void;
}

const STEPS = [
	{ id: 1, name: 'Basic Info', title: 'Basic details' },
	{ id: 2, name: 'Pricing Model', title: 'Pricing Model' },
	{ id: 3, name: 'Discount Rate', title: 'Discount Rate' },
	{ id: 4, name: 'Pass-Through Costs', title: 'Pass-Through Costs' },
	{ id: 5, name: 'Monthly & Batch Fees', title: 'Monthly & Batch Fees' },
];

const PricingConfigurationWizard: React.FC<PricingConfigurationWizardProps> = ({
	isOpen,
	onClose,
	onComplete,
	editConfigId,
	isFullPage = false,
	onBack,
}) => {
	const [currentStep, setCurrentStep] = useState(1);
	const [configuration, setConfiguration] = useState<PricingConfiguration>({
		name: '',
		description: '',
		is_default: true,
		rate_plan_type: 'IC_PLUS',
		selected_rate_types: ['IC_PLUS'],
		discount_rate_mode: 'same_for_all',
		authorization_fee_mode: 'same_for_all',
		pass_through_cost_mode: 'standard',
		standard_discount_rate: 0.8,
		standard_authorization_fee: 0.2,
		use_standard_assessment_rates: true,
		use_bundled_amex_rate: false,
		custom_rates: {},
		// Rate-type-specific modes for independence
		ic_plus_discount_rate_mode: 'same_for_all',
		ic_plus_authorization_fee_mode: 'same_for_all',
		tiered_discount_rate_mode: 'same_for_all',
		tiered_authorization_fee_mode: 'same_for_all',
		tiered_same_as_credit_6tier: true,
		flat_tiered_discount_rate_mode: 'same_for_all',
		flat_tiered_authorization_fee_mode: 'same_for_all',
	});

	const [createEnhancedCustomRates, { isLoading: isCreating }] =
		useCreateEnhancedCustomRatesMutation();
	const [updateEnhancedCustomRates, { isLoading: isUpdating }] =
		useUpdateEnhancedCustomRatesMutation();
	const { data: existingConfigResponse, isLoading: isLoadingConfig } =
		useGetEnhancedCustomRatesQuery(editConfigId || '', {
			skip: !editConfigId,
		});
	const { data: defaultRates, isLoading: isLoadingDefaultRates } =
		useGetDefaultRatesQuery();
	const { showToast } = useToast();

	// Load existing configuration when editing
	useEffect(() => {
		if (editConfigId && existingConfigResponse?.data) {
			// Transform enhanced API response to wizard state
			const transformedConfig = transformEnhancedResponseToWizard(
				existingConfigResponse.data,
			);
			setConfiguration(transformedConfig);
		}
	}, [editConfigId, existingConfigResponse]);

	// Load default rates when creating new configuration
	useEffect(() => {
		if (!editConfigId && defaultRates && defaultRates.custom_rates) {
			// Initialize with default configuration
			const defaultConfig = createDefaultPricingConfiguration();
			setConfiguration(defaultConfig);
		}
	}, [editConfigId, defaultRates]);

	const updateConfiguration = (updates: Partial<PricingConfiguration>) => {
		setConfiguration((prev) => ({ ...prev, ...updates }));
	};

	const isCurrentStepValid = () => {
		switch (currentStep) {
			case 1:
				return configuration.name.trim() !== '';
			case 2:
				return (
					configuration.selected_rate_types &&
					configuration.selected_rate_types.length > 0
				);
			case 3:
				return true;
			case 4:
				return true;
			case 5:
				return true;
			default:
				return false;
		}
	};

	const handleSave = async () => {
		try {
			// Transform wizard state to enhanced API request
			const enhancedRequest = transformWizardToEnhancedRequest(configuration);

			let result;
			if (editConfigId) {
				// Update existing configuration
				result = await updateEnhancedCustomRates({
					id: editConfigId,
					config: enhancedRequest,
				});
			} else {
				// Create new configuration
				result = await createEnhancedCustomRates(enhancedRequest);
			}

			if ('data' in result && result.data?.success) {
				showToast('Success', 'Configuration saved successfully!', 'success');
				onComplete(result.data.data.id);
			} else if ('error' in result) {
				const errorMessage =
					(result.error as any)?.data?.message ||
					'Failed to save configuration';
				showToast('Error', errorMessage, 'error');
			} else {
				showToast('Error', 'Failed to save configuration', 'error');
			}
		} catch (error) {
			console.error('Error saving configuration:', error);
			showToast('Error', 'Failed to save configuration', 'error');
		}
	};

	const handleNext = () => {
		if (currentStep < STEPS.length && isCurrentStepValid()) {
			setCurrentStep(currentStep + 1);
		}
	};

	const handlePrevious = () => {
		if (currentStep > 1) {
			setCurrentStep(currentStep - 1);
		}
	};

	const renderNavigationButtons = () => {
		const isFirstStep = currentStep === 1;
		const isLastStep = currentStep === STEPS.length;
		const isStepValid = isCurrentStepValid();
		const isNextDisabled = !editConfigId && !isStepValid;

		return (
			<div className="flex justify-between items-center">
				{/* Left Button */}
				<button
					onClick={isFirstStep ? onClose : handlePrevious}
					className="w-[200px] flex justify-center items-center px-8 py-3 bg-white border border-gray-300 hover:bg-gray-100 disabled:bg-gray-400 text-gray-500 rounded-lg transition-colors font-medium disabled:opacity-50 disabled:cursor-not-allowed"
				>
					{!isFirstStep && <ChevronLeft className="w-5 h-5 mr-1" />}
					{isFirstStep ? 'Cancel' : 'Previous'}
				</button>

				{/* Center - Step Indicator */}
				<div className="flex items-center text-gray-500">
					<span className="text-md">
						Step {currentStep} of {STEPS.length}
					</span>
				</div>

				{/* Right Button */}
				<button
					onClick={isLastStep ? handleSave : handleNext}
					disabled={isCreating || isUpdating || isNextDisabled}
					className="w-[200px] flex justify-center items-center px-8 py-3 bg-purple-500 hover:bg-purple-600 disabled:bg-gray-400 text-white rounded-lg transition-colors font-medium disabled:opacity-50 disabled:cursor-not-allowed"
				>
					{isLastStep ? (
						<>
							{isCreating || isUpdating ? (
								<>
									<div className="animate-spin rounded-full h-5 w-5 border-b-2 border-white mr-2"></div>
									{editConfigId ? 'Updating...' : 'Creating...'}
								</>
							) : (
								<>
									<Check className="w-5 h-5 mr-1" />
									{editConfigId ? 'Update' : 'Create'}
								</>
							)}
						</>
					) : (
						<>
							Next
							<ChevronRight className="w-5 h-5 ml-1" />
						</>
					)}
				</button>
			</div>
		);
	};

	const renderCurrentStep = () => {
		switch (currentStep) {
			case 1:
				return (
					<BasicInfoStep
						configuration={configuration}
						onUpdate={updateConfiguration}
						onNext={handleNext}
						onCancel={onClose}
						hideNavigation={isFullPage}
					/>
				);
			case 2:
				return (
					<PricingModelStep
						configuration={configuration}
						onUpdate={updateConfiguration}
						onNext={handleNext}
						onPrevious={handlePrevious}
						hideNavigation={isFullPage}
					/>
				);
			case 3:
				return (
					<DiscountRateStep
						configuration={configuration}
						onUpdate={updateConfiguration}
						onNext={handleNext}
						onPrevious={handlePrevious}
						hideNavigation={isFullPage}
						enhancedApiResponse={existingConfigResponse?.data}
					/>
				);
			case 4:
				return (
					<PassThroughCostsStep
						configuration={configuration}
						onUpdate={updateConfiguration}
						onSave={() => setCurrentStep(5)}
						isLoading={isCreating || isUpdating}
						onPrevious={handlePrevious}
						hideNavigation={isFullPage}
					/>
				);
			case 5:
				return (
					<MonthlyAndBatchFeesStep
						configuration={configuration}
						onUpdate={updateConfiguration}
						onSave={handleSave}
						isLoading={isCreating || isUpdating}
						onPrevious={handlePrevious}
						hideNavigation={isFullPage}
					/>
				);
			default:
				return null;
		}
	};

	// Show loading state when fetching existing config or default rates
	if (
		(editConfigId && isLoadingConfig) ||
		(!editConfigId && isLoadingDefaultRates)
	) {
		return (
			<div className="min-h-screen bg-gray-50 flex items-center justify-center">
				<div className="text-center">
					<div className="animate-spin rounded-full h-12 w-12 border-b-2 border-purple-500 mx-auto mb-4"></div>
					<p className="text-gray-600">Loading configuration...</p>
				</div>
			</div>
		);
	}

	// Show error state if config failed to load
	if (editConfigId && !isLoadingConfig && !existingConfigResponse?.data) {
		return (
			<div className="min-h-screen bg-gray-50 flex items-center justify-center">
				<div className="text-center">
					<p className="text-red-600 mb-4">Failed to load configuration</p>
					<button
						onClick={onClose}
						className="px-4 py-2 bg-gray-500 text-white rounded hover:bg-gray-600"
					>
						Close
					</button>
				</div>
			</div>
		);
	}

	const containerClasses = isFullPage
		? 'h-[calc(100vh-110px)] mt-10 bg-gray-50 flex flex-col'
		: 'fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50';

	const contentClasses = isFullPage
		? 'w-full max-w-6xl mx-auto flex-1 flex flex-col min-h-0'
		: 'bg-white rounded-lg shadow-xl w-full max-w-4xl max-h-[90vh] overflow-hidden';

	return (
		<div className={containerClasses}>
			<div className={contentClasses}>
				<div className="flex flex-col h-full">
					{/* Header */}
					<div
						className={`flex-shrink-0 flex items-center justify-between ${isFullPage ? 'mb-8' : 'p-6 border-b border-gray-200'}`}
					>
						<div className="flex items-center space-x-4">
							{isFullPage && onBack && (
								<button
									onClick={onBack}
									className="flex items-center px-4 py-2 bg-white border border-gray-300 text-gray-700 rounded-lg hover:bg-gray-50 transition-colors text-sm font-medium"
								>
									<ArrowLeft className="w-4 h-4 mr-2" />
									Back
								</button>
							)}
							<h2 className="text-2xl font-semibold text-gray-900">
								{editConfigId
									? 'Edit Configuration'
									: 'Create New Configuration'}
							</h2>
						</div>
						{!isFullPage && (
							<button
								onClick={onClose}
								className="text-gray-400 hover:text-gray-600 transition-colors"
							>
								<X className="w-6 h-6" />
							</button>
						)}
					</div>

					{/* Progress Bar */}
					<div
						className={`flex-shrink-0 ${isFullPage ? 'mb-8' : 'px-6 py-4 border-b border-gray-200'}`}
					>
						<div className="flex items-center justify-between mb-2">
							{STEPS.map((step, index) => {
								// In edit mode: all steps are clickable
								// In create mode: only allow clicking on current step or completed valid steps
								const isClickable = editConfigId
									? true
									: step.id <= currentStep;
								const canClick = isClickable;

								return (
									<div
										key={step.id}
										className={`flex w-full items-center justify-center p-3 border border-gray-200 rounded-lg mx-1 ${
											canClick
												? 'cursor-pointer hover:bg-gray-50'
												: 'cursor-not-allowed opacity-50'
										}`}
										onClick={() => canClick && setCurrentStep(step.id)}
									>
										<div
											className={`w-6 h-6 rounded-full flex items-center justify-center text-sm font-medium ${
												currentStep >= step.id
													? 'bg-purple-500 text-white'
													: 'bg-gray-200 text-gray-600'
											}`}
										>
											{currentStep > step.id ? (
												<Check className="w-4 h-4" />
											) : (
												step.id
											)}
										</div>
										<span
											className={`ml-2 text-sm font-medium ${
												currentStep >= step.id
													? 'text-purple-600'
													: 'text-gray-500'
											}`}
										>
											{step.title}
										</span>
									</div>
								);
							})}
						</div>
					</div>

					{/* Content - Scrollable */}
					<div
						className={`flex-1 overflow-y-auto min-h-0 bg-white p-6 border border-gray-200 rounded-lg`}
					>
						{renderCurrentStep()}
					</div>

					{/* Navigation Footer - Fixed */}
					{isFullPage && (
						<div className="flex-shrink-0 py-6">
							{renderNavigationButtons()}
						</div>
					)}
				</div>
			</div>
		</div>
	);
};

export default PricingConfigurationWizard;
